import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ExternalLink, FileText, RefreshCw, Building2, User, Calendar, Search, Filter, Plus } from 'lucide-react';
import { toast } from 'sonner';

// In-memory cache for invoices data
interface CacheEntry {
  data: Invoice[];
  timestamp: number;
  expiresAt: number;
}

const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes in milliseconds
let invoicesCache: CacheEntry | null = null;

interface Project {
  id: string;
  name: string;
}

interface Company {
  id: string;
  name: string;
}

interface Contact {
  id: string;
  name: string;
}

interface Invoice {
  id: string; // This is now the display Invoice ID (e.g., "KJ-INV-0463")
  notionInvID: string; // This is the main ID used for navigation and internal references
  amount: number;
  currency: string;
  dueDate: string;
  tax: boolean;
  companyName: Company[] | null;
  contactName: Contact[] | null;
  status: 'Paid' | 'Open' | 'Planned';
  xeroId?: string;
  xeroUrl?: string;
  project: Project[] | null;
  sentByPost?: boolean;
  sentByEmail?: boolean;
  createdAt?: string;
  updatedAt?: string;
  tenantID?: string;
}

export default function InvoicesPage() {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [billingEntityFilter, setBillingEntityFilter] = useState<string>('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [visibleCounts, setVisibleCounts] = useState({
    Planned: 5,
    Open: 5,
    Paid: 5
  });
  const navigate = useNavigate();

  // Get WordPress config for asset URLs
  const wpConfig = typeof window !== 'undefined' ? (window as any).WP_COCKPIT : null;
  const invoiceIconUrl = wpConfig?.assetsUrl 
    ? wpConfig.assetsUrl.replace('/assets/', '/') + 'invoiceIcon.svg'
    : '/invoiceIcon.svg';

  const getBillingEntity = (tenantID: string | null | undefined) => {
    if (!tenantID) {
      return { name: 'Not Set', variant: 'secondary' as const, className: 'bg-gray-100 text-gray-600 border-gray-300' };
    }
    
    switch (tenantID) {
      case '75b62360-50ab-40e7-944c-c65197ba7b68':
        return { name: 'Kainjoo SA', variant: 'default' as const, className: 'bg-primary text-primary-foreground border-primary' };
      case 'abc6d49d-db14-4f90-99f9-e4231b1cc274':
        return { name: 'Kainjoo Inc', variant: 'secondary' as const, className: 'bg-blue-100 text-blue-800 border-blue-300' };
      default:
        return { name: 'Unknown Entity', variant: 'secondary' as const, className: 'bg-yellow-100 text-yellow-800 border-yellow-300' };
    }
  };

  const handleCreateInvoice = async (tenantID: string) => {
    try {
      setShowCreateDialog(false);
      
      // Get the webhook URL from WordPress config
      const webhookUrl = wpConfig?.invoiceCreateUrl || 'https://n8n.kjoo.io/webhook/invoices/create/getcreationdata';
      
      // Show loading toast
      toast.loading('Getting invoice creation data...', { id: 'create-invoice' });
      
      // Call the webhook with the selected tenantID
      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ tenantID }),
      });

      if (!response.ok) {
        throw new Error(`Failed to get creation data: ${response.status}`);
      }

      const creationData = await response.json();
      
      // For now, just show success and log the data
      // Later we'll implement the actual invoice creation form with this data
      console.log('Invoice creation data received:', creationData);
      
      toast.success('Invoice creation data retrieved successfully!', { id: 'create-invoice' });
      
      // TODO: Open invoice creation form with creationData
      // For now, just show an info toast
      toast.info('Invoice creation form will be implemented in the next step');
      
    } catch (error) {
      console.error('Error getting invoice creation data:', error);
      toast.error('Failed to get invoice creation data. Please try again.', { id: 'create-invoice' });
    }
  };

  const fetchInvoices = async (forceRefresh = false) => {
    try {
      // Check in-memory cache first (unless force refresh)
      if (!forceRefresh && invoicesCache) {
        const now = Date.now();
        if (now < invoicesCache.expiresAt) {
          const wp = (window as any).WP_COCKPIT;
          const isDev = wp?.devMode || false;
          if (isDev) console.log('[InvoicesPage] Using cached data, expires in:', Math.round((invoicesCache.expiresAt - now) / 1000 / 60), 'minutes');
          setInvoices(invoicesCache.data);
          setLoading(false);
          return;
        } else {
          const wp = (window as any).WP_COCKPIT;
          const isDev = wp?.devMode || false;
          if (isDev) console.log('[InvoicesPage] Cache expired, fetching fresh data');
          invoicesCache = null; // Clear expired cache
        }
      }

      if (forceRefresh) {
        setRefreshing(true);
        invoicesCache = null; // Clear cache on force refresh
        toast.info('Fetching latest invoice data... This may take up to 60 seconds.');
      } else {
        setLoading(true);
      }

      const endpoint = '/wp-json/kj-cockpit/v1/fetch-invoices' + (forceRefresh ? '?force=1' : '');
      
      // Build headers with nonce
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      // Add WP REST nonce header if provided by server-localized object
      try {
        const wp = (window as any).WP_COCKPIT;
        const isDev = wp?.devMode || false;
        if (isDev) {
          console.log('[fetchInvoices] Force refresh:', forceRefresh);
          console.log('[fetchInvoices] URL:', endpoint);
          console.log('[fetchInvoices] WP_COCKPIT object:', wp);
        }
        if (wp && wp.restNonce) {
          headers['X-WP-Nonce'] = String(wp.restNonce);
          if (isDev) console.log('[fetchInvoices] Added nonce to headers:', headers['X-WP-Nonce']);
        } else {
          console.error('No restNonce found in WP_COCKPIT');
        }
      } catch (e) {
        console.error('Error accessing WP_COCKPIT:', e);
      }

      const fetchOptions: RequestInit = { 
        credentials: 'same-origin', 
        headers 
      };
      if (forceRefresh) {
        fetchOptions.cache = 'no-store';
      }

      const response = await fetch(endpoint, fetchOptions);

      if (!response.ok) {
        if (response.status === 502) {
          throw new Error('The server is taking longer than expected to process the request. Please try again in a moment.');
        } else if (response.status === 504) {
          throw new Error('Request timed out. The invoice data processing is taking longer than expected.');
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      }

      const data = await response.json();
      
      if (data.success && data.data) {
        const now = Date.now();
        
        // Store in in-memory cache
        invoicesCache = {
          data: data.data,
          timestamp: now,
          expiresAt: now + CACHE_DURATION
        };
        
        setInvoices(data.data);
        if (forceRefresh) {
          toast.success('Invoices refreshed successfully');
        }
        
        const wp = (window as any).WP_COCKPIT;
        const isDev = wp?.devMode || false;
        if (isDev) console.log('[InvoicesPage] Data cached until:', new Date(invoicesCache.expiresAt).toLocaleTimeString());
      } else {
        console.error('Failed to fetch invoices:', data);
        toast.error('Failed to load invoices');
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast.error('Error loading invoices');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'No due date';
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(new Date(dateString));
  };

  const getDueDateStatus = (dueDate: string, status: string) => {
    if (!dueDate) return null;
    
    // Don't show overdue status for paid invoices
    if (status === 'Paid') return null;
    
    const due = new Date(dueDate);
    const now = new Date();
    const diffTime = due.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return { label: 'Overdue', variant: 'destructive' as const, className: 'badge-overdue' };
    } else if (diffDays <= 7) {
      return { label: 'Due Soon', variant: 'secondary' as const };
    } else if (diffDays <= 30) {
      return { label: 'Due This Month', variant: 'outline' as const };
    }
    return null;
  };

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'Paid':
        return { variant: 'default' as const, className: 'badge-paid' };
      case 'Open':
        return { variant: 'secondary' as const, className: 'badge-open' };
      case 'Planned':
        return { variant: 'outline' as const, className: 'badge-planned' };
      default:
        return { variant: 'outline' as const, className: 'badge-planned' };
    }
  };

  // Helper functions to safely extract values from arrays
  const getAllCompanies = (companyArr: Company[] | null): Company[] => {
    return companyArr || [];
  };

  const getAllContacts = (contactArr: Contact[] | null): Contact[] => {
    return contactArr || [];
  };

  const getAllProjects = (projectArr: Project[] | null): Project[] => {
    return projectArr || [];
  };

  // Search function that searches across invoice ID, company, contact, and project names
  const searchInvoices = (invoices: Invoice[], term: string): Invoice[] => {
    if (!term.trim()) return invoices;
    
    const searchLower = term.toLowerCase();
    return invoices.filter(invoice => {
      // Search in invoice ID
      if (invoice.id.toLowerCase().includes(searchLower)) return true;
      
      // Search in company names
      if (invoice.companyName?.some(company => 
        company.name.toLowerCase().includes(searchLower)
      )) return true;
      
      // Search in contact names
      if (invoice.contactName?.some(contact => 
        contact.name.toLowerCase().includes(searchLower)
      )) return true;
      
      // Search in project names
      if (invoice.project?.some(project => 
        project.name.toLowerCase().includes(searchLower)
      )) return true;
      
      return false;
    });
  };

  // Filter and group invoices
  const processedInvoices = useMemo(() => {
    let filtered = searchInvoices(invoices, searchTerm);
    
    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(invoice => invoice.status === statusFilter);
    }
    
    // Apply billing entity filter
    if (billingEntityFilter !== 'all') {
      if (billingEntityFilter === 'not-set') {
        filtered = filtered.filter(invoice => !invoice.tenantID);
      } else {
        filtered = filtered.filter(invoice => invoice.tenantID === billingEntityFilter);
      }
    }
    
    // Sort by due date within each status (descending - newest first)
    const sortByDueDate = (a: Invoice, b: Invoice) => {
      const dateA = new Date(a.dueDate || '1900-01-01'); // Put invoices without due date at the end
      const dateB = new Date(b.dueDate || '1900-01-01');
      return dateB.getTime() - dateA.getTime(); // Reversed for descending order
    };
    
    // Group by status with priority order: Planned, Open, Paid
    const statusOrder = ['Planned', 'Open', 'Paid'];
    const grouped = statusOrder.reduce((acc, status) => {
      const statusInvoices = filtered
        .filter(invoice => invoice.status === status)
        .sort(sortByDueDate);
      
      if (statusInvoices.length > 0) {
        acc[status] = statusInvoices;
      }
      
      return acc;
    }, {} as Record<string, Invoice[]>);
    
    return grouped;
  }, [invoices, searchTerm, statusFilter, billingEntityFilter]);

  // Function to load more invoices for a specific status
  const loadMoreForStatus = (status: string) => {
    setVisibleCounts(prev => ({
      ...prev,
      [status]: prev[status as keyof typeof prev] + 5
    }));
  };

  // Reset visible counts when search or filter changes
  useEffect(() => {
    setVisibleCounts({
      Planned: 5,
      Open: 5,
      Paid: 5
    });
  }, [searchTerm, statusFilter, billingEntityFilter]);

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <Skeleton className="h-8 w-32 mb-2" />
            <Skeleton className="h-4 w-64" />
          </div>
          <Skeleton className="h-10 w-24" />
        </div>
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-40" />
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center space-x-4">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-28" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <img 
            src={invoiceIconUrl} 
            alt="Invoice Icon"
            className="w-8 h-8"
            onError={(e) => {
              // Fallback to FileText icon if SVG fails to load
              (e.target as HTMLImageElement).style.display = 'none';
              const fallback = (e.target as HTMLImageElement).nextElementSibling as HTMLElement;
              if (fallback) fallback.style.display = 'block';
            }}
          />
          <FileText className="w-8 h-8 text-primary" style={{ display: 'none' }} />
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Invoices</h1>
            <p className="text-muted-foreground">
              Manage and track your invoices from Xero
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setShowCreateDialog(true)}
            className="flex items-center gap-2"
            variant="default"
          >
            <Plus className="h-4 w-4" />
            Add Invoice
          </Button>
          <Button
            onClick={() => fetchInvoices(true)}
            disabled={refreshing}
            variant="outline"
            className="flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Processing...' : 'Refresh'}
          </Button>
        </div>
      </div>

      {/* Search and Filter Controls */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Search & Filter
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search by invoice ID, company, contact, or project..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="Planned">Planned</SelectItem>
                  <SelectItem value="Open">Open</SelectItem>
                  <SelectItem value="Paid">Paid</SelectItem>
                </SelectContent>
              </Select>
              <Select value={billingEntityFilter} onValueChange={setBillingEntityFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Filter by entity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Entities</SelectItem>
                  <SelectItem value="75b62360-50ab-40e7-944c-c65197ba7b68">Kainjoo SA</SelectItem>
                  <SelectItem value="abc6d49d-db14-4f90-99f9-e4231b1cc274">Kainjoo Inc</SelectItem>
                  <SelectItem value="not-set">Not Set</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Grouped Invoice Tables */}
      {Object.keys(processedInvoices).length === 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Invoice List
            </CardTitle>
            <CardDescription>
              No invoices found matching your criteria
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8">
              <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-lg font-medium">No invoices found</p>
              <p className="text-muted-foreground">
                {searchTerm ? 'Try adjusting your search terms' : 'No invoices are currently available'}
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(processedInvoices).map(([status, statusInvoices]) => {
            const visibleCount = visibleCounts[status as keyof typeof visibleCounts] || 5;
            const visibleInvoices = statusInvoices.slice(0, visibleCount);
            const hasMore = statusInvoices.length > visibleCount;
            
            return (
              <Card key={status}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Badge 
                        variant={getStatusVariant(status).variant} 
                        className={`text-sm ${getStatusVariant(status).className}`}
                      >
                        {status}
                      </Badge>
                      <span className="text-lg font-semibold">{status} Invoices</span>
                      <span className="text-sm text-muted-foreground">
                        ({statusInvoices.length} total)
                      </span>
                    </div>
                    {hasMore && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => loadMoreForStatus(status)}
                      >
                        Load More ({statusInvoices.length - visibleCount} remaining)
                      </Button>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Invoice ID</TableHead>
                          <TableHead>Amount</TableHead>
                          <TableHead>Due Date</TableHead>
                          <TableHead>Company</TableHead>
                          <TableHead>Contact</TableHead>
                          <TableHead>Project</TableHead>
                          <TableHead>Tax</TableHead>
                          <TableHead>Billing Entity</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {visibleInvoices.map((invoice) => {
                          const dueDateStatus = getDueDateStatus(invoice.dueDate, invoice.status);
                          
                          return (
                            <TableRow 
                              key={invoice.notionInvID} 
                              className="cursor-pointer hover:bg-muted/50"
                              onClick={() => navigate(`/invoices/${invoice.notionInvID}`)}
                            >
                              <TableCell className="font-medium">
                                {invoice.id}
                              </TableCell>
                              <TableCell>
                                <div className="font-medium">
                                  {formatCurrency(invoice.amount, invoice.currency)}
                                </div>
                                <div className="text-sm text-muted-foreground">
                                  {invoice.currency}
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex flex-col gap-1">
                                  <div className="flex items-center gap-2">
                                    <Calendar className="h-4 w-4 text-muted-foreground" />
                                    <span className="text-sm">
                                      {formatDate(invoice.dueDate)}
                                    </span>
                                  </div>
                                  {dueDateStatus && (
                                    <Badge 
                                      variant={dueDateStatus.variant} 
                                      className={`w-fit ${dueDateStatus.className || ''}`}
                                    >
                                      {dueDateStatus.label}
                                    </Badge>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <Building2 className="h-4 w-4 text-muted-foreground" />
                                  <div className="flex flex-wrap gap-1">
                                    {(() => {
                                      const companies = getAllCompanies(invoice.companyName);
                                      return companies.length > 0 ? (
                                        companies.map((company, index) => (
                                          <Badge key={index} variant="outline" className="text-xs">
                                            {company.name}
                                          </Badge>
                                        ))
                                      ) : (
                                        <span className="text-sm text-muted-foreground">No company</span>
                                      );
                                    })()}
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <User className="h-4 w-4 text-muted-foreground" />
                                  <div className="flex flex-wrap gap-1">
                                    {(() => {
                                      const contacts = getAllContacts(invoice.contactName);
                                      return contacts.length > 0 ? (
                                        contacts.map((contact, index) => (
                                          <Badge key={index} variant="outline" className="text-xs">
                                            {contact.name}
                                          </Badge>
                                        ))
                                      ) : (
                                        <span className="text-sm text-muted-foreground">No contact</span>
                                      );
                                    })()}
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex flex-wrap gap-1">
                                  {(() => {
                                    const projects = getAllProjects(invoice.project);
                                    return projects.length > 0 ? (
                                      projects.map((project, index) => (
                                        <Button
                                          key={index}
                                          variant="ghost"
                                          size="sm"
                                          className="h-auto p-1 text-left justify-start"
                                          onClick={(e) => {
                                            e.stopPropagation(); // Prevent row click
                                            // Navigate to project page - you can implement this based on your routing
                                            console.log('Navigate to project:', project.id);
                                          }}
                                        >
                                          <Badge variant="outline" className="text-xs">
                                            {project.name}
                                          </Badge>
                                        </Button>
                                      ))
                                    ) : (
                                      <span className="text-sm text-muted-foreground">No project</span>
                                    );
                                  })()}
                                </div>
                              </TableCell>
                              <TableCell>
                                <Badge variant={invoice.tax ? "default" : "secondary"}>
                                  {invoice.tax ? "Tax Inc." : "No Tax"}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                {(() => {
                                  const entity = getBillingEntity(invoice.tenantID);
                                  return (
                                    <Badge 
                                      variant={entity.variant}
                                      className={`${entity.className} text-xs`}
                                    >
                                      {entity.name}
                                    </Badge>
                                  );
                                })()}
                              </TableCell>
                              <TableCell>
                                {invoice.xeroUrl && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    asChild
                                    className="h-8 w-8 p-0"
                                    onClick={(e) => e.stopPropagation()} // Prevent row click
                                  >
                                    <a
                                      href={invoice.xeroUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      title="View in Xero"
                                    >
                                      <ExternalLink className="h-4 w-4" />
                                    </a>
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Invoice Creation Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Create New Invoice</DialogTitle>
            <DialogDescription>
              Select a billing entity to create a new invoice. This will determine the organization context for the invoice.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-3">
              <h4 className="text-sm font-medium">Select Billing Entity:</h4>
              <div className="space-y-2">
                <Button
                  variant="outline"
                  className="w-full justify-start h-auto p-4"
                  onClick={() => handleCreateInvoice('75b62360-50ab-40e7-944c-c65197ba7b68')}
                >
                  <div className="flex items-center justify-between w-full">
                    <div className="text-left">
                      <div className="font-medium">Kainjoo SA</div>
                      <div className="text-sm text-muted-foreground">Swiss Entity</div>
                    </div>
                    <Badge variant="default" className="bg-primary text-primary-foreground">
                      SA
                    </Badge>
                  </div>
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start h-auto p-4"
                  onClick={() => handleCreateInvoice('abc6d49d-db14-4f90-99f9-e4231b1cc274')}
                >
                  <div className="flex items-center justify-between w-full">
                    <div className="text-left">
                      <div className="font-medium">Kainjoo Inc</div>
                      <div className="text-sm text-muted-foreground">US Entity</div>
                    </div>
                    <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                      Inc
                    </Badge>
                  </div>
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}